import useSWR from 'swr';
import { fallbackClients, fallbackServices, fallbackTestimonials, galleryItems } from '@site/data';

const json = async (res) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Something went wrong. Please try again.');
  return body;
};

const fetcher = (url) => fetch(url, { headers: { Accept: 'application/json' } }).then(json);

const options = { revalidateOnFocus: false, shouldRetryOnError: false };

/** Live rows from a public list endpoint, or the bundled fallback until the API answers / when it is empty. */
function useList(url, fallback, map = (x) => x) {
  const { data } = useSWR(url, fetcher, { ...options, fallbackData: { items: [] } });
  const live = (data?.items || []).map(map);
  return live.length ? live : fallback;
}

const splitLines = (text) =>
  String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

/** Services with `points` split into a list. Optional category filter. */
export function useServices(category) {
  const all = useList('/api/public/services', fallbackServices).map((s) => ({ ...s, pointList: splitLines(s.points) }));
  return category ? all.filter((s) => s.category === category) : all;
}

/** Client logos for marquees and the clientele grid. */
export function useClients() {
  return useList('/api/public/clients', fallbackClients).map((c) => ({ src: c.logo, alt: c.name }));
}

export function useTestimonials() {
  return useList('/api/public/testimonials', fallbackTestimonials);
}

/** Gallery photos with categories. */
export function useGallery() {
  return useList('/api/public/gallery', galleryItems, (p) => ({
    src: p.image,
    alt: p.alt || 'Armed Fire Services project photo',
    category: p.category,
  }));
}

/**
 * Live Google rating + positive reviews from the server (key stays server-side),
 * with the CMS `settings.google` values as the fallback summary.
 */
export function useGoogleReviews(fallback = {}) {
  const { data } = useSWR('/api/public/google-reviews', fetcher, { ...options, fallbackData: null });
  const live = data && data.configured && !data.error ? data : null;
  return {
    live: Boolean(live),
    rating: live?.rating ?? (Number(fallback.rating) || null),
    count: live?.count ?? (Number(fallback.count) || null),
    mapsUrl: live?.mapsUrl || fallback.mapsUrl || null,
    writeReviewUrl: live?.writeReviewUrl || fallback.reviewUrl || fallback.mapsUrl || null,
    reviews: live?.reviews || [],
  };
}

export function submitEnquiry(payload) {
  return fetch('/api/public/enquiries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  }).then(json);
}
